import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {Router} from '@angular/router';
import {EntityHeader} from '../entity/entity-header';
import {EntitySearchCriteria} from '../entity/entity-search-criteria';
import {PersonLogin} from '../person/person-login';
import {SelectionItem} from '../selections/selection-item';
import {EntityService} from '../services/entity-service';
import {InputCacheService} from '../services/input-cache-service';
import {PersonLoginService} from '../services/person-login-service';
import {SelectionService} from '../services/selection-service';
import {StringUtils} from '../utils/string-utils';
import {MatSort, MatSortHeader, Sort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {FileUtils} from "../utils/file-utils";
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import {OpportunityService} from '../services/opportunity-service';
import {OpportunitySearchCriteria} from './opportunity-search-criteria';

@Component({
    selector: 'opportunity-master',
    templateUrl: './opportunity-master.component.html',
    styleUrls: ['./opportunity-master.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatPaginator, MatProgressSpinner, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, NgClass, CurrencyPipe, DatePipe]
})
export class OpportunityMasterComponent implements OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly entityService = inject(EntityService);
  private readonly selectionService = inject(SelectionService);
  private readonly inputCacheService = inject(InputCacheService);
  private readonly opportunityService = inject(OpportunityService);
  private readonly router = inject(Router);


  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  private personLogin!: PersonLogin;
  searchResults: MatTableDataSource<EntityHeader> = new MatTableDataSource<EntityHeader>();
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  criteria!: EntitySearchCriteria;
  entityName = 'OPPORTUNITY';
  readonly warningMessage = signal<string>(undefined!);
  readonly organizations = signal<SelectionItem[]>(undefined!);
  readonly regions = signal<SelectionItem[]>(undefined!);
  displayedColumns: string[] = ['entityId', 'projectName', 'associatedEntityId', 'salesRep', 'responsibility', 'outsideSalesRep',
    'includeInForecastIndicator', 'finalExtendedAmount', 'totalUnitQty', 'statusDescription', 'currentPhase',
    'awardProbabilityPercent', 'lastStatusChangeDate', 'updateTimestamp', 'region'];

  search(): void {
    this.clearStatus();
    this.inputCacheService.criteria = this.criteria;

    let opportunitySearchCriteria = new OpportunitySearchCriteria();
    if (!opportunitySearchCriteria.orgUnitId) {
      opportunitySearchCriteria.orgUnitId = this.criteria.orgUnitId;
    }
    if (!opportunitySearchCriteria.region) {
      opportunitySearchCriteria.region = this.criteria.region;
    }

    this.opportunityService.searchHeaders(opportunitySearchCriteria).subscribe({
      next: results => {
        results = results.filter(result => result.associatedEntityId.startsWith("MR01")
          || result.associatedEntityId.startsWith("SR01") ||  result.associatedEntityId.startsWith("QT01"));

        results.forEach(result => result.entityId = result.id.entityId + ';' + result.id.entityRevision);
        this.searchResults = new MatTableDataSource(results);
        this.searchResults.sort = this.sort;
        let sortState: Sort = this.getSortState();
        if (StringUtils.isNotBlank(sortState?.direction)) {
          this.sort.active = sortState?.active;
          this.sort.direction = sortState?.direction;
          this.searchResults.sort.sortChange.emit(sortState)
        }
        this.searchResults.paginator = this.paginator;
        this.isBusy.set(false);
        if (results.length === 200) {
          this.warningMessage.set('These results might not include some older entries, please provide more criteria to shorten the list');
        }
      }, error: error => this.handleError(error)
    });
  }

  public selectOpportunity(header: EntityHeader) {
    this.router.navigate(['opportunities/' + header.id.entityId + '/' + header.id.entityRevision]);
  }

  private clearStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.searchResults = null!;
    this.warningMessage.set(null!);
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.criteria = this.inputCacheService.criteria || new EntitySearchCriteria();
      this.criteria.entityName = this.entityName;
      if (!this.criteria.orgUnitId) {
        this.criteria.orgUnitId = '';
      }
      if (!this.criteria.region) {
        this.criteria.region = '';
      }
      this.personLogin = this.personLoginService.getCachedLogin()!;
      if (!this.personLogin) {
        this.router.navigate(['main']);
      } else {
        this.selectionService.getEmulatedOrganizations().subscribe(results => this.organizations.set(results));
        this.selectionService.getActiveRegionOrgs().subscribe(results => this.regions.set(results));
        this.search();
      }
    }
  }

  public exportToExcel(): void {
    this.isBusy.set(true);
    this.entityService.getListTemplate(this.criteria).subscribe({
      next: (response) => {
        this.isBusy.set(false);
        FileUtils.downloadFile(response, 'Opportunites.xlsx');
      }, error: (error) => this.handleError(error)
    });
  }

  getStatusColor(header: EntityHeader) {
    if (!header || !header.colorCode || header.colorCode.length === 0) {
      return '';
    }
    switch (header.colorCode) {
      case '1Red':
        return 'redRow';
      case '2Yellow':
        return 'yellowRow';
      case '3Green':
        return 'greenRow';
      default:
        return '';
    }
  }

  getSortState(): Sort {

    let activeColumn: any = "{}";
    let direction: any = "{}";
    if(sessionStorage.getItem('opportunityMasterSortActive') != null){
      activeColumn = JSON.parse(sessionStorage.getItem('opportunityMasterSortActive')!);
    }
    if(sessionStorage.getItem('opportunityMasterSortDirection') != null){
      direction = JSON.parse(sessionStorage.getItem('opportunityMasterSortDirection')!);
    }

    //const activeColumn = JSON.parse(sessionStorage.getItem('opportunityMasterSortActive'));
    //const direction: string = JSON.parse(sessionStorage.getItem('opportunityMasterSortDirection'));
    const parsedColumn: any = activeColumn === "{}" ? undefined : JSON.parse(activeColumn);
    const parsedDirection: any = direction === "{}" ? undefined : JSON.parse(direction);
    return <Sort>{
      active: parsedColumn,
      direction: parsedDirection
    }
  }

  setSortState() {
    sessionStorage.setItem('opportunityMasterSortActive', JSON.stringify(this.searchResults.sort!.active || '{}'));
    sessionStorage.setItem('opportunityMasterSortDirection', JSON.stringify(this.searchResults.sort!.direction || '{}'));
  }

}
